import React from 'react';
import styled from 'styled-components';
import {useTranslation} from 'react-i18next';
import {ControlButton, TscButtonType} from '@techsmith/tsc-cloud-style-guide';
import LocalMediaImporter from './LocalMediaImporter';
import uploadApi from '../../../service/uploadApi';
import {MinWidthButtonWrapper} from '../../util/StyledElements';
import UploadProgressModal from './UploadProgressModal';
import Axios, {AxiosProgressEvent, CancelTokenSource} from 'axios';
import {UpsertQueryParamInUrl} from '../../util/QueryParamsHelper';
import Constants from '../../../constants/Constants';
import tabNames from '../../../constants/tabNames';
import '../../../static/css/modal.less';
import cssConstants from '../../../constants/cssConstants';
import WarningWithFaqLink from '../../util/WarningWithFaqLink';
import {IFileUploadCandidate} from '../../../model/fileUploadCandidate';
import withMemoizedContexts from '../../../context/contextContainerHoC';
import {useTscTheme} from '../../../context/themeProvider';
import {invalidateMediaCache, mediaStore} from '../../../context/mediaProvider';
import {updateMediaOperationsState} from '../../../context/mediaOperationsProvider';
import {AlertModalPortal, BasicModalPortal} from '../../util/ModalPortalHelper';
import config from '../../../service/config';

const MediaPreviewWrapper = styled.div`
   display: flex;
   object-fit: contain;
   justify-content: center;
   max-height: 30rem;
   max-width: 30rem;
   margin-bottom: 1rem;
`;

export const VideoPreview = styled.video`
   max-height: ${cssConstants.defaultModalWidth};
   max-width: ${cssConstants.defaultModalWidth};
   height: 100%;
   width: 100%;
`;

export const ImagePreview = styled.img`
   max-height: ${cssConstants.defaultModalWidth};
   max-width: ${cssConstants.defaultModalWidth};
   height: 100%;
   width: 100%;
`;

const ButtonGroup = styled.div`
   margin-top: 1rem;
`;

export const ReplaceMediaModalBase: React.FC<IReplaceMediaModalProps & IStateMappedProps> = ({onClose, Hash}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [state, setState] = React.useState({
      cancelTokenSource: null,
      errorText: '',
      file: null,
      fileSelected: false,
      fileUploading: false,
      showConfirmationModal: false,
      showFileSelectionModal: true,
      showUploadProgressModal: false,
      uploadProgress: 0
   } as IReplaceMediaModalState);

   const updateState = (updatedValues: Partial<IReplaceMediaModalState>): void => {
      setState(prevState => Object.assign({}, prevState, updatedValues));
   };

   const onUploadProgress = (progressEvent: AxiosProgressEvent): void => {
      const near100Percent = 95;
      const percentCompleted = Math.min(Math.round((progressEvent.loaded * 100) / progressEvent.total), near100Percent);
      updateState({uploadProgress: percentCompleted});
   };

   const isVideo = (file: File): boolean => file.type.includes('video');

   const onFileSelect = (uploadCandidate: IFileUploadCandidate) => {
      updateState({fileSelected: true, file: uploadCandidate.file});
   };

   const confirmReplaceMediaClick = (): void => {
      updateState({showConfirmationModal: true});
   };

   const replaceMedia = async (): Promise<void> => {
      try {
         updateMediaOperationsState({IsReplacingMedia: true});
         const cancelTokenSource = Axios.CancelToken.source();
         updateState({cancelTokenSource: cancelTokenSource});
         await uploadApi.replaceMedia(Hash, state.file, onUploadProgress, cancelTokenSource);
         updateState({uploadProgress: 100, fileUploading: false});
         await invalidateMediaCache(Hash);
         window.location.assign(UpsertQueryParamInUrl(window.location.href, Constants.queryParams.tab, tabNames.details));
         updateMediaOperationsState({IsReplacingMedia: false});
      } catch (e) {
         let errorText = '';
         if (!Axios.isCancel(e)) {
            if (e.status === Constants.statusCodes.unsupportedMediaType) {
               errorText = t('uploadMedia.fileTypeWarningVideo');
            } else {
               errorText = t('general.errorText');
            }
         }
         updateState({showConfirmationModal: false, fileUploading: false, uploadProgress: 0, errorText: errorText, showUploadProgressModal: false, showFileSelectionModal: true});
         updateMediaOperationsState({IsReplacingMedia: false});
      }
   };

   const replaceMediaClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      updateState({showConfirmationModal: false, showUploadProgressModal: true, showFileSelectionModal: false, fileUploading: true});
      await replaceMedia();
   };

   const onCancelClick = (): void => {
      if (state.file && state.fileSelected) {
         updateState({file: null, fileSelected: false, errorText: ''});
      } else {
         onClose();
      }
   };

   const onFileSelectionModalClose = (): void => {
      updateState({showFileSelectionModal: false, showUploadProgressModal: state.fileUploading});
      !state.fileUploading && onClose();
   };

   const onConfirmationModalClose = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
      e.stopPropagation();
      updateState({showConfirmationModal: false});
   };

   const onUploadProgressModalClose = (): void => {
      if (!state.fileUploading && state.uploadProgress === 100) {
         onClose();
      } else {
         updateState({showUploadProgressModal: false, showFileSelectionModal: true});
      }
   };

   const handleUploadCancelRequest = (): void => {
      state.cancelTokenSource?.cancel();
   };

   const getWarningWithFaqLinkUrl = (): string => {
      if (config.featureSwitches.IsScreencast) {
         return Constants.navigation.screencastFAQLink;
      }
      return Constants.navigation.knowmiaFAQLink;
   };

   const buttonGroup: React.JSX.Element = (
      <ButtonGroup className="button-group">
         <MinWidthButtonWrapper>
            <ControlButton
               label={state.fileSelected ? t('general.back') : t('general.cancel')}
               buttonType={TscButtonType.secondary}
               disabled={false}
               themeName={theme}
               onClick={onCancelClick}
               testId="replace-media-modal-cancel-button"
            />
         </MinWidthButtonWrapper>
         {state.fileSelected && (
            <ControlButton
               label={t('replaceMedia.action')}
               buttonType={TscButtonType.primary}
               disabled={!state.fileSelected}
               themeName={theme}
               onClick={confirmReplaceMediaClick}
               testId="replace-media-modal-replace-media-button"
            />
         )}
      </ButtonGroup>
   );

   const mediaPreview: React.JSX.Element = state.file && (
      <MediaPreviewWrapper>
         {isVideo(state.file) ? (
            <VideoPreview
               data-test-id="replace-media-video-preview"
               src={URL.createObjectURL(state.file)}
               controls={true}
               autoPlay={false}
            />
         )
            : (
               <ImagePreview
                  data-test-id="replace-media-image-preview"
                  src={URL.createObjectURL(state.file)}
               />
            )}
      </MediaPreviewWrapper>
   );

   const fileSelectModal: React.JSX.Element = (
      <BasicModalPortal
         visible={true}
         title={t('uploadMedia.action')}
         onClose={onFileSelectionModalClose}
         themeName={theme}
         testId="replace-media-modal"
      >
         <div className="modal-body">
            <div className="form-body themeable-section">
               {state.errorText && (
                  <WarningWithFaqLink linkUrl={getWarningWithFaqLinkUrl()}>
                     {state.errorText}
                  </WarningWithFaqLink>
               )}
               {!state.fileSelected && (
                  <LocalMediaImporter
                     onFileSelect={onFileSelect}
                     allowMultiple={false}
                     ignoreQuotaLimits={true}
                  />
               )}
               {(state.fileSelected && !state.fileUploading) && mediaPreview}
               {buttonGroup}
            </div>
         </div>
      </BasicModalPortal>
   );

   const replaceConfirmationAlertModal: React.JSX.Element = (
      <AlertModalPortal
         title={t('replaceMedia.action')}
         onClose={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => onConfirmationModalClose(e)}
         onConfirm={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => replaceMediaClick(e)}
         confirmButtonType={TscButtonType.destructive}
         confirmContent={t('replaceMedia.button')}
         onDismiss={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => onConfirmationModalClose(e)}
         dismissButtonType={TscButtonType.secondary}
         dismissContent={t('general.cancel')}
      >
         {config.featureSwitches.IsScreencast ? t('replaceMedia.confirmationScreencast') : t('replaceMedia.confirmation')}
      </AlertModalPortal>
   );

   return (
      <>
         {state.showFileSelectionModal && fileSelectModal}
         {state.showConfirmationModal && replaceConfirmationAlertModal}
         {state.showUploadProgressModal && (
            <UploadProgressModal
               uploadProgress={state.uploadProgress}
               cancelUpload={handleUploadCancelRequest}
               onClose={onUploadProgressModalClose}
            />
         )}
      </>
   );
};

export interface IReplaceMediaModalProps {
   onClose(): void;
}

export interface IReplaceMediaModalState {
   cancelTokenSource: CancelTokenSource;
   errorText: string;
   file: File;
   fileSelected: boolean;
   fileUploading: boolean;
   showConfirmationModal: boolean;
   showFileSelectionModal: boolean;
   showUploadProgressModal: boolean;
   uploadProgress: number;
}

export interface IStateMappedProps {
   Hash: string;
}

const mapStatesToProps = (
   {onClose}: IReplaceMediaModalProps,
   {Hash}: Partial<IStateMappedProps>
): IStateMappedProps & IReplaceMediaModalProps => ({onClose, Hash});

export default withMemoizedContexts(mapStatesToProps, mediaStore)(ReplaceMediaModalBase);
